import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Spinner } from "@phosphor-icons/react";
import { API, COVERS } from "@/lib/site";

export const Ebooks = () => {
  const [ebooks, setEbooks] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    fetch(`${API}/ebooks`)
      .then((r) => r.json())
      .then((d) => {
        setEbooks(d.ebooks || []);
        setBundle(d.bundle || null);
      })
      .catch(() => toast.error("Could not load the ebook library."));
  }, []);

  const buy = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key,
          quantity: 1,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error();
      }
    } catch {
      toast.error("Checkout could not start. Please try again.");
      setLoadingKey(null);
    }
  };

  return (
    <section id="ebooks" className="relative py-24 lg:py-32 bg-brand-surface2/40 border-y border-brand-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="overline">The library</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-brand-text mt-4 max-w-2xl">
              Ebooks to start tonight.
            </h2>
          </div>
          <p className="text-brand-dim max-w-sm">
            Secure checkout via Stripe. Your download link lands in your inbox
            the moment payment clears.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {ebooks.map((e, i) => (
            <motion.article
              key={e.lookup_key}
              data-testid={`ebook-card-${e.lookup_key}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group flex flex-col"
            >
              <div className="relative overflow-hidden rounded-2xl border border-brand-line bg-brand-bg">
                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
                <img
                  src={COVERS[e.lookup_key]}
                  alt={e.title}
                  className="w-full h-[420px] object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
                <span className="absolute top-4 left-4 z-20 overline glass rounded-full px-3 py-1 border border-brand-line">
                  {e.pillar}
                </span>
              </div>

              <div className="pt-6 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-brand-text">{e.title}</h3>
                <p className="text-sm text-brand-dim mt-1">{e.subtitle}</p>
                {e.author && (
                  <p className="text-xs text-brand-gold mt-1">by {e.author}</p>
                )}
                <p className="text-brand-dim/80 text-sm mt-4 leading-relaxed flex-1">
                  {e.blurb}
                </p>
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-brand-line">
                  <span className="font-serif text-3xl text-brand-text">
                    ${e.price.toFixed(0)}
                  </span>
                  <button
                    data-testid={`ebook-buy-${e.lookup_key}`}
                    onClick={() => buy(e.lookup_key)}
                    disabled={loadingKey === e.lookup_key}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-text px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors duration-300 disabled:opacity-60"
                  >
                    {loadingKey === e.lookup_key ? (
                      <>
                        <Spinner size={16} className="animate-spin" /> Starting
                      </>
                    ) : (
                      <>
                        Buy now <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {bundle && (
          <motion.div
            data-testid="ebook-bundle"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 rounded-3xl border border-brand-gold/40 bg-gradient-to-br from-brand-surface to-brand-bg p-8 lg:p-12 grid lg:grid-cols-12 gap-10 items-center overflow-hidden relative"
          >
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-brand-gold/10 blur-3xl" />
            <div className="lg:col-span-5 flex items-end justify-center gap-3">
              {bundle.items.map((k, i) => (
                <img
                  key={k}
                  src={COVERS[k]}
                  alt={k}
                  style={{ zIndex: 3 - i }}
                  className={`w-1/3 rounded-lg border border-brand-line shadow-2xl ${
                    i === 1 ? "-mx-4 mb-4 scale-110" : "opacity-90"
                  }`}
                />
              ))}
            </div>
            <div className="lg:col-span-7 relative z-10">
              <span className="overline">Best value</span>
              <h3 className="font-serif text-3xl lg:text-4xl text-brand-text mt-3">
                {bundle.title}
              </h3>
              <p className="text-brand-dim mt-3 max-w-xl">{bundle.blurb}</p>
              <div className="flex items-center gap-4 mt-6">
                {(() => {
                  const total = ebooks.reduce((s, e) => s + (e.price || 0), 0);
                  const save = total > 0 ? Math.round((1 - bundle.price / total) * 100) : 0;
                  return (
                    <>
                      <span className="font-serif text-4xl text-brand-text">${bundle.price.toFixed(0)}</span>
                      {total > bundle.price && (
                        <span className="text-brand-dim line-through">${total.toFixed(0)}</span>
                      )}
                      {save > 0 && <span className="overline text-brand-sage">Save {save}%</span>}
                    </>
                  );
                })()}
              </div>
              <button
                data-testid="ebook-buy-bundle"
                onClick={() => buy(bundle.lookup_key)}
                disabled={loadingKey === bundle.lookup_key}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-gold px-8 py-4 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors duration-300 disabled:opacity-60"
              >
                {loadingKey === bundle.lookup_key ? (
                  <>
                    <Spinner size={16} className="animate-spin" /> Starting
                  </>
                ) : (
                  <>
                    Get all three <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
